import { inject, Injectable } from '@angular/core';
import { FeatureCheckerService, PermissionCheckerService } from 'abp-ng2-module';
import { AppSessionService } from '../session/app-session.service';

import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot } from '@angular/router';
import { isArray } from 'lodash-es';
import { FeatureRouteDataModel } from '@shared/models/feature-route-data.model';

@Injectable()
export class AppRouteGuard {
    constructor(
        private _permissionChecker: PermissionCheckerService,
        private _featureChecker: FeatureCheckerService,
        private _router: Router,
        private _sessionService: AppSessionService,
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        if (!this._sessionService.user) {
            this._router.navigate(['/account/login']);
            return false;
        }

        if (!route.data || (!route.data['permission'] && !route.data['feature'])) {
            return true;
        }

        const permissions = route.data['permission'];
        const feature = route.data['feature'];

        if (this.checkPermission(permissions) && this.checkFeature(feature)) {
            return true;
        }

        this._router.navigate([this.selectBestRoute()]);
        return false;
    }

    canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean {
        return this.canActivate(route, state);
    }

    selectBestRoute(): string {
        if (!this._sessionService.user) {
            return '/account/login';
        }

        if (this._permissionChecker.isGranted('Pages.Users')) {
            return '/app/admin/users';
        }

        return '/app/home';
    }

    checkPermission(data: string | string[]): boolean {
        if (!data) {
            return true;
        }

        if (Array.isArray(data)) {
            return data.some((item: string) => this._permissionChecker.isGranted(item));
        }

        return this._permissionChecker.isGranted(data);
    }

    checkFeature(data: FeatureRouteDataModel | FeatureRouteDataModel[]): boolean {
        const isFeatureVisible = (featureTarget: FeatureRouteDataModel): boolean => featureTarget.featureVisibilityFn(this._featureChecker.isEnabled(featureTarget.feature));

        if (!data) {
            return true;
        }

        if (isArray(data)) {
            return data.some(isFeatureVisible);
        }

        return isFeatureVisible(data);
    }
}

export const authRouteGuard: CanActivateFn = (route: ActivatedRouteSnapshot, state: RouterStateSnapshot) => {
    return inject(AppRouteGuard).canActivate(route, state);
};
